import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type ReactNode,
} from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';

import { initials } from '@web/ui/core/utils/initials';

/*
 * Avatar — Radix Avatar wrapped to DS v2 sizes + tones.
 *
 * Examples:
 *   <Avatar size="md"><AvatarImage src={url} alt={name} /><AvatarFallback displayName={name} /></Avatar>
 *   <Avatar size="sm" tone="ai"><AvatarFallback>AI</AvatarFallback></Avatar>
 *   <Avatar size="md" tone="green"><AvatarFallback displayName={name} /></Avatar>
 *
 * Sizes and tones are locked by the design system. The component does not
 * accept a `className` prop — every visual decision lives in the variant
 * table here.
 */
const avatarVariants = cva(
  'relative inline-flex flex-shrink-0 items-center justify-center overflow-visible rounded-pill border bg-muted text-foreground font-medium',
  {
    variants: {
      size: {
        sm: 'h-5.5 w-5.5 text-mono-xs',
        md: 'h-row-h-compact w-row-h-compact text-tiny',
        lg: 'h-9 w-9 text-base',
        xl: 'h-12 w-12 text-section-title',
      },
      tone: {
        neutral: 'border-border-soft',
        brand: 'bg-primary-tint text-primary-ink border-primary/20',
        ai: 'bg-ai-tint text-ai-ink border-ai/20',
        vip: 'bg-vip-tint text-vip border-vip/20',
        green: 'bg-tone-green-tint text-tone-green-ink border-tone-green-fg/20',
        amber: 'bg-tone-amber-tint text-tone-amber-ink border-tone-amber-fg/20',
        red: 'bg-tone-red-tint text-tone-red-ink border-tone-red-fg/20',
      },
    },
    defaultVariants: { size: 'md', tone: 'neutral' },
  },
);

interface AvatarProps
  extends
    Omit<ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>, 'className'>,
    VariantProps<typeof avatarVariants> {
  children: ReactNode;
}

export const Avatar = forwardRef<ComponentRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  function Avatar({ size, tone, children, ...props }, ref) {
    return (
      <AvatarPrimitive.Root ref={ref} className={avatarVariants({ size, tone })} {...props}>
        <span className="absolute inset-0 overflow-hidden rounded-pill">{children}</span>
      </AvatarPrimitive.Root>
    );
  },
);

interface AvatarFallbackProps extends Omit<
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>,
  'className'
> {
  displayName?: string | null;
  fallback?: string;
}

export const AvatarFallback = forwardRef<
  ComponentRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(function AvatarFallback({ displayName, fallback = '?', children, ...props }, ref) {
  const content = displayName == null ? children : initials(displayName, fallback);

  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className="flex h-full w-full items-center justify-center"
      {...props}
    >
      {content}
    </AvatarPrimitive.Fallback>
  );
});
